import React, { useState } from "react";
import { toast } from "sonner";
import { FilePlus2, Lock } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { openUpgrade } from "@/components/sg/UpgradeDialog";

export function ChangeOrderButton({ project, onChange }) {
  const { user } = useAuth();
  const allowed = user?.role === "owner" || user?.plan === "super_premium";
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", amount: "", description: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/milestones`, { ...f, amount: Number(f.amount) });
      onChange(r.data); setOpen(false); setF({ name: "", amount: "", description: "" });
      toast.success("Change order added. Click “Request approval” to get it signed.");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!allowed) {
    return (
      <button onClick={() => openUpgrade("Change orders")} data-testid="change-order-locked"
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 h-8 px-3 rounded-md border border-dashed border-border transition-colors">
        <Lock className="h-3 w-3" />Add change order
      </button>
    );
  }
  return (
    <>
      <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="add-change-order-btn" onClick={() => setOpen(true)}><FilePlus2 className="h-3.5 w-3.5 mr-1" />Add change order</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a change order</DialogTitle>
            <DialogDescription>Extra work your client asked for. It's added to the contract total and needs their signature like any milestone.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>What's changing</Label><Input data-testid="change-order-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Add recessed lighting (6 fixtures)" /></div>
            <div><Label>Price (USD)</Label><Input data-testid="change-order-amount" type="number" min="1" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="850" /></div>
            <div><Label>Details (optional)</Label><Textarea data-testid="change-order-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} maxLength={600} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="change-order-submit" disabled={busy || f.name.trim().length < 2 || !(Number(f.amount) > 0)} onClick={submit} className="bg-indigo-600 hover:bg-indigo-500 text-white">{busy ? "Adding…" : "Add change order"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
